export const LARGE_FILE_THRESHOLD_BYTES = 60 * 1024 * 1024;
export const DEFAULT_CHUNK_SIZE_BYTES = 10 * 1024 * 1024;

const shouldLogMultipartUploads =
	typeof process !== "undefined" &&
	process?.env?.NODE_ENV !== "production";

const createUploadSessionId = () =>
	`${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const splitFileIntoChunks = (file, chunkSizeBytes = DEFAULT_CHUNK_SIZE_BYTES) => {
	if (!file) return [];

	const size = Number(file?.size ?? 0);
	if (size <= 0) {
		return [
			{
				file,
				meta: {
					isChunked: false,
					chunkIndex: 1,
					totalChunks: 1,
					chunkSize: 0,
					originalFileName: file?.name ?? "",
					originalFileSize: 0,
					uploadId: createUploadSessionId(),
				},
			},
		];
	}

	const totalChunks = Math.max(1, Math.ceil(size / chunkSizeBytes));
	const uploadId = createUploadSessionId();
	const chunks = [];

	for (let index = 0; index < totalChunks; index += 1) {
		const start = index * chunkSizeBytes;
		const end = Math.min(start + chunkSizeBytes, size);
		const chunkBlob = file.slice(start, end, file?.type);
		const chunkName =
			totalChunks === 1
				? file?.name
				: `${file?.name}.part-${String(index + 1).padStart(4, "0")}-of-${String(
						totalChunks
				  ).padStart(4, "0")}`;

		chunks.push({
			file: chunkBlob,
			meta: {
				isChunked: totalChunks > 1,
				chunkIndex: index + 1,
				totalChunks,
				chunkSize: end - start,
				originalFileName: file?.name ?? "",
				originalFileSize: size,
				uploadId,
				chunkName,
				isFirstChunk: index === 0,
				isLastChunk: index === totalChunks - 1,
			},
		});
	}

	return chunks;
};

export const uploadFileWithChunking = async ({
	file,
	uploadPart,
	thresholdBytes = LARGE_FILE_THRESHOLD_BYTES,
	chunkSizeBytes = DEFAULT_CHUNK_SIZE_BYTES,
}) => {
	if (!file) {
		throw new Error("A file is required for upload");
	}

	if (typeof uploadPart !== "function") {
		throw new Error("uploadPart must be a function");
	}

	if (Number(file?.size ?? 0) <= thresholdBytes) {
		return uploadPart({
			file,
			meta: {
				isChunked: false,
				chunkIndex: 1,
				totalChunks: 1,
				chunkSize: Number(file?.size ?? 0),
				originalFileName: file?.name ?? "",
				originalFileSize: Number(file?.size ?? 0),
				uploadId: createUploadSessionId(),
				chunkName: file?.name ?? "",
				isFirstChunk: true,
				isLastChunk: true,
			},
		});
	}

	const chunks = splitFileIntoChunks(file, chunkSizeBytes);
	if (shouldLogMultipartUploads) {
		console.log("[Multipart Upload] chunking enabled", {
			fileName: file?.name,
			fileSize: Number(file?.size ?? 0),
			thresholdBytes,
			chunkSizeBytes,
			totalChunks: chunks.length,
		});
	}

	let latestResponse;

	for (const chunk of chunks) {
		if (shouldLogMultipartUploads) {
			console.log("[Multipart Upload] uploading chunk", {
				fileName: file?.name,
				chunkIndex: chunk?.meta?.chunkIndex,
				totalChunks: chunk?.meta?.totalChunks,
				chunkSize: chunk?.meta?.chunkSize,
				chunkName: chunk?.meta?.chunkName,
			});
		}

		latestResponse = await uploadPart(chunk);
	}

	return latestResponse;
};
